/**
 * PostHog and the visitor id (`os_vid`).
 *
 * The visitor id is set by the www edge worker on the first request, or minted by this app when
 * there is none (lib/visitor-id.ts). open-api saves it in the CDP row at sign-up, so the same value
 * on PostHog events joins them: `events.properties.visitor_id = cdp_signup_source.visitor_id`.
 *
 * It is a plain property, never PostHog's distinct id. The id outlives logout, so using it as the
 * distinct id would merge two people who share a browser.
 */

import type { CaptureResult } from 'posthog-js';
import { getExistingVisitorId } from './visitor-id';

export const VISITOR_ID_PROPERTY = 'visitor_id';

// Replay chunks are frequent and large, and a recording already belongs to a session.
const EVENTS_WITHOUT_VISITOR_ID = new Set(['$snapshot']);

/**
 * `before_send` hook: stamps the current visitor id on an outgoing event. Set at send time and not
 * with `posthog.register`, because `posthog.reset()` wipes registered properties at logout, and
 * registered properties persist in PostHog's cookie, which www and the app share.
 */
export function addVisitorId(event: CaptureResult | null): CaptureResult | null {
  if (!event || EVENTS_WITHOUT_VISITOR_ID.has(event.event)) return event;
  const visitorId = getExistingVisitorId();
  if (visitorId) event.properties[VISITOR_ID_PROPERTY] = visitorId;
  return event;
}

/**
 * The `$set_once` person properties for identify. Set once, so a person keeps the id from their
 * first identification, which is their sign-up's, when they later use another device.
 */
export function visitorIdPersonProperties(): Record<string, string> | undefined {
  const visitorId = getExistingVisitorId();
  return visitorId ? { [VISITOR_ID_PROPERTY]: visitorId } : undefined;
}
